import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, computed, signal, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, map, merge, switchMap, tap, Subject } from 'rxjs';
import { ImageVisualizerComponent } from '../image-visualizer/image-visualizer.component';
import { CoordinateSpace, ImageSize, ImageVisualizerBox } from '../image-visualizer/image-visualizer.types';
import { RecognitionSystemLibraryService } from '../../../recongition-system-api/recongnition-library';
import { ImageRecognitionExtend, RecognitionDetails } from '../../../recongition-system-api/model';
import { MatTabsModule } from '@angular/material/tabs';
import { MeasurementsPanelComponent } from '../measurements-panel/measurements-panel.component';
import { DetectionsPanelComponent } from '../detections-panel/detections-panel.component';
import { MetadataCardComponent } from '../metadata-card/metadata-card.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PageVisualizerService } from '../page-visualizer.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NavigationTrackerService } from '../../../services/navigation_tracker.service';

@Component({
  selector: 'app-page-visualizer',
  standalone: true,
  imports: [
    CommonModule,
    ImageVisualizerComponent,
    MatTabsModule,
    MetadataCardComponent,
    MeasurementsPanelComponent,
    DetectionsPanelComponent,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './page-visualizer.component.html',
  styleUrl: './page-visualizer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageVisualizerComponent {
  @Input() set eventId(val: string) {
    if (val) this.manualId$.next(val);
  }
  @Input() set hideCloseOption(val: boolean) {
    this.hideCloseButton = val;
  }
  private readonly manualId$ = new Subject<string>();
  readonly coordinateSpace: CoordinateSpace = 'image';
  readonly recognitionId = signal('');
  readonly isLoading = signal(true);
  readonly loadingState = signal<'loading' | 'ready' | 'timeout'>('loading');
  readonly loadingMessage = computed(() => {
    if (this.loadingState() === 'timeout') {
      return 'No hay resultados aún. Puede que el reconocimiento no exista o siga procesándose.';
    }
    return 'Cargando resultados del reconocimiento...';
  });
  readonly images = signal<ImageRecognitionExtend[]>([]);
  readonly selectedImage = signal<ImageRecognitionExtend | null>(null);
  readonly selectedIndex = signal(0);
  readonly recognitionDetails = signal<Record<string | number, RecognitionDetails>>({});
  readonly selectedBoxId = signal<string | number | null>(null);
  readonly hiddenBoxIds = signal<Set<string | number>>(new Set());
  readonly imageSize = signal<ImageSize | null>(null);
  readonly filterBoxes = signal(true);
  readonly details = computed(() => {
    const image = this.selectedImage();
    if (!image) return null;
    return this.recognitionDetails()[image.image_id] ?? null;
  });
  readonly metadataFields = computed(() => this.details()?.client_metadata_schema ?? null);

  readonly imageSrc = computed(() => this.selectedImage()?.url ?? '');
  readonly boxes = computed(() => {
    const hidden = this.hiddenBoxIds();
    const filterBoxes = this.filterBoxes();
    return this.buildBoxes()
      .filter((box) => !filterBoxes || box.metadata?.['task_rule_inferences'] === true)
      .filter((box) => !hidden.has(box.id));
  });
  readonly rules = computed(() => this.details()?.rules ?? []);
  private loadingTimeoutId?: number;

  readonly zoomLevel = signal(1);
  readonly minZoom = 1;
  readonly maxZoom = 6;
  readonly zoomStep = 0.2;
  hideCloseButton: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private recognition: RecognitionSystemLibraryService,
    private snackBar: MatSnackBar,
    private visualizerService: PageVisualizerService,
    private router: Router,
    private navigation_service: NavigationTrackerService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    const recognitionId$ = merge(
      this.manualId$,
      this.route.paramMap.pipe(map((params) => params.get('id') ?? '')),
      this.route.queryParamMap.pipe(
        map((params) =>
          params.get('event_id') ?? params.get('recognition_id') ?? params.get('id') ?? ''
        ),
      ),
    ).pipe(
      filter((id) => id.length > 0),
      distinctUntilChanged(),
      tap((id) => this.resetState(id)),
    );

    recognitionId$
      .pipe(
        switchMap((id) => this.recognition.getRecognitionDetailsLive(id)),
        takeUntilDestroyed(),
      )
      .subscribe((payload) => this.updateRecognitionDetails(payload));

    recognitionId$
      .pipe(
        switchMap((id) => this.recognition.getRecognitionImagesWithImagesLive(id)),
        takeUntilDestroyed(),
      )
      .subscribe((payload) => this.updateImages(payload));
  }

  private readonly gestureHandler = (event: Event) => {
    event.preventDefault();
  };

  ngOnInit(): void {
    if (window.matchMedia && window.matchMedia('(max-width: 600px)').matches) {
      this.document.addEventListener('gesturestart', this.gestureHandler, { passive: false });
      this.document.addEventListener('gesturechange', this.gestureHandler, { passive: false });
      this.document.addEventListener('gestureend', this.gestureHandler, { passive: false });
    }
    this.route.queryParams.subscribe(queryParams => {
      const closeValue = queryParams['close'];
      if (closeValue !== undefined) {
        this.hideCloseButton = (closeValue === 'false') ? true : false;
      }
    });
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('gesturestart', this.gestureHandler);
    this.document.removeEventListener('gesturechange', this.gestureHandler);
    this.document.removeEventListener('gestureend', this.gestureHandler);
  }

  onBoxClick(box: ImageVisualizerBox): void {
    this.selectedBoxId.set(box.id);
  }

  onBoxHover(_box: ImageVisualizerBox | null): void {
    // Placeholder for tooltip-driven state or external sync.
  }

  onBoxToggle(payload: { id: string | number; visible: boolean }): void {
    this.hiddenBoxIds.update((current) => {
      const next = new Set(current);
      if (payload.visible) {
        next.delete(payload.id);
      } else {
        next.add(payload.id);
        if (this.selectedBoxId() === payload.id) {
          this.selectedBoxId.set(null);
        }
      }
      return next;
    });
  }

  onFilterBoxesChange(checked: boolean): void {
    this.filterBoxes.set(checked);
  }

  private resetState(recognitionId: string): void {
    this.recognitionId.set(recognitionId);
    this.images.set([]);
    this.selectedImage.set(null);
    this.recognitionDetails.set({});
    this.selectedBoxId.set(null);
    this.setLoadingState('loading');
    this.startLoadingTimeout();
  }

  private updateImages(images: ImageRecognitionExtend[]): void {
    this.images.set(images);

    if (!images.length) {
      this.selectedImage.set(null);
      this.selectedIndex.set(0);
      this.updateLoadingState();
      return;
    }

    const current = this.selectedImage();
    if (!current) {
      this.selectedImage.set(images[0]);
      this.selectedIndex.set(0);
      this.updateLoadingState();
      return;
    }

    const matchIndex = images.findIndex((img) => img.image_id === current.image_id);
    if (matchIndex === -1) {
      this.selectedImage.set(images[0]);
      this.selectedIndex.set(0);
      this.updateLoadingState();
      return;
    }

    this.selectedImage.set(images[matchIndex]);
    this.selectedIndex.set(matchIndex);
    this.updateLoadingState();
  }

  private updateRecognitionDetails(details: RecognitionDetails[]): void {
    const mapped = details.reduce((result, item) => {
      if (item.image?.image_id !== undefined && item.image?.image_id !== null) {
        result[item.image.image_id] = item;
      }
      return result;
    }, {} as Record<string | number, RecognitionDetails>);

    this.recognitionDetails.set(mapped);
    this.updateLoadingState();
  }

  private updateLoadingState(): void {
    const detail = this.details();
    if (!detail) {
      if (this.loadingState() !== 'timeout') {
        this.setLoadingState('loading');
      }
      return;
    }

    const hasInferences = Array.isArray(detail.inferences) && detail.inferences.length > 0;
    const isFinished = detail.finished_at !== undefined && detail.finished_at !== null;

    if (hasInferences || isFinished) {
      this.setLoadingState('ready');
      return;
    }
    if (this.loadingState() !== 'timeout') {
      this.setLoadingState('loading');
    }
  }

  private startLoadingTimeout(): void {
    if (this.loadingTimeoutId) {
      window.clearTimeout(this.loadingTimeoutId);
    }
    this.loadingTimeoutId = window.setTimeout(() => {
      if (this.loadingState() !== 'ready') {
        this.setLoadingState('timeout');
      }
    }, 45000);
  }

  private setLoadingState(state: 'loading' | 'ready' | 'timeout'): void {
    this.loadingState.set(state);
    this.isLoading.set(state !== 'ready');
    if (state === 'ready' && this.loadingTimeoutId) {
      window.clearTimeout(this.loadingTimeoutId);
      this.loadingTimeoutId = undefined;
    }
  }

  private buildBoxes(): ImageVisualizerBox[] {
    const image = this.selectedImage();
    if (!image) return [];

    const detail = this.recognitionDetails()[image.image_id];
    const inferences = detail?.inferences ?? [];

    return this.visualizerService.mapInferencesToBoxes(inferences);
  }

  selectImageByIndex(index: number): void {
    const list = this.images();
    if (!list.length) return;
    const clamped = Math.max(0, Math.min(index, list.length - 1));
    this.selectedImage.set(list[clamped]);
    this.selectedIndex.set(clamped);
    this.updateLoadingState();
  }

  selectImage(image: ImageRecognitionExtend): void {
    const list = this.images();
    const index = list.findIndex((img) => img.image_id === image.image_id);
    if (index === -1) return;
    this.selectedImage.set(list[index]);
    this.selectedIndex.set(index);
    this.updateLoadingState();
  }

  prevImage(): void {
    this.selectImageByIndex(this.selectedIndex() - 1);
  }

  nextImage(): void {
    this.selectImageByIndex(this.selectedIndex() + 1);
  }

  trackByImage(_index: number, item: ImageRecognitionExtend): number {
    return item.image_id ?? item.id ?? _index;
  }

  zoomIn(): void {
    this.setZoom(this.zoomLevel() + this.zoomStep);
  }

  zoomOut(): void {
    this.setZoom(this.zoomLevel() - this.zoomStep);
  }

  resetZoom(): void {
    this.setZoom(1);
  }

  onZoomChange(value: number): void {
    this.zoomLevel.set(this.normalizeZoom(value));
  }

  private setZoom(value: number): void {
    this.zoomLevel.set(this.normalizeZoom(value));
  }

  private normalizeZoom(value: number): number {
    const clamped = Math.min(Math.max(value, this.minZoom), this.maxZoom);
    return Number(clamped.toFixed(2));
  }

  copyIdRecon(id: string | undefined | null): void {
    if (!id) return;
    navigator.clipboard.writeText(id).then(() => {
      this.snackBar.open('Id copiado con éxito.', 'ok', { duration: 2000 });
    });
  }

  onShared(): void {
    if (!navigator.share) {
      this.copyToClipboard();
      return;
    }
    this.shareUrl();
  }

  private copyToClipboard(): void {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(
      () => {
        this.snackBar.open('URL copiada al portapapeles.', 'ok', { duration: 2000 });
      },
      () => {
        this.snackBar.open('No se pudo copiar la URL.', 'Cerrar', { duration: 3000 });
      },
    );
  }

  private shareUrl(): void {
    const url = window.location.href;
    const details = this.details();
    navigator.share?.({
      title: '¡Mira mis resultados!',
      text: `Encuentra aquí los resultados de tu reconocimiento: ${details?.recognition_id ?? ''}`,
      url,
    })
      .catch(() => {
        this.snackBar.open('No se pudo compartir la URL.', 'Cerrar', { duration: 3000 });
      });
  }

  calculateDurationInSeconds(startedAt?: Date | string, finishedAt?: Date | string): number {
    return this.visualizerService.calculateDurationInSeconds(startedAt, finishedAt);
  }

  async downloadCsv(): Promise<void> {
    const id = this.recognitionId();
    if (!id) return;
    await this.visualizerService.downloadCsv(id);
  }

  backbutton() {
    this.router.navigateByUrl(this.navigation_service.previousUrl || '/playground/task');
  }
}
